import { z } from 'zod';

/**
 * Teacher reports — posting banked points to salary (Phase 5 of
 * docs/teacher-reports-implementation-plan.md, §6.4).
 */

export const incentivePostSchema = z.object({ salaryCycleId: z.string().uuid() }).strict();

export const incentiveReverseSchema = z.object({ reason: z.string().trim().min(3).max(500) }).strict();

/** Why a teacher's points can't be posted into this cycle yet. They stay banked for a later posting. */
export type IncentiveLineProblem = 'missing_payee' | 'payment_paid';

export interface IncentivePreviewLine {
  teacherId: string;
  teacherName: string;
  /** "2026-09" — the month the points were earned in. */
  earnedMonth: string;
  points: number;
  /** Already paid for this earned month by earlier postings. */
  alreadyPaidPaise: number;
  amountPaise: number;
  /** The month's cap cut the amount. */
  capped: boolean;
  /** Paid after its own month's posting ("approved late"). */
  late: boolean;
  salaryPaymentId: string | null;
  problem: IncentiveLineProblem | null;
}

export interface IncentivePostingDto {
  id: string;
  status: 'posted' | 'reversed';
  totalPoints: number;
  totalPaise: number;
  ratePaise: number;
  capPaise: number;
  postedAt: string;
  postedByName: string | null;
  reversedAt: string | null;
  reverseReason: string | null;
  lineCount: number;
  /** Every salary payment it touched is still unpaid. */
  reversible: boolean;
}

export interface IncentivePreviewResponse {
  cycle: { id: string; year: number; month: number; payoutDate: string; isHeld: boolean };
  /** Points earned before this month are paid ("2026-10" → September and earlier). */
  paysBefore: string;
  ratePaise: number;
  capPaise: number;
  lines: IncentivePreviewLine[];
  totals: { points: number; amountPaise: number; teachers: number; skipped: number };
  /** Points still waiting for approval in the months this cycle pays. Paid in a later posting once approved. */
  pendingPoints: number;
  postings: IncentivePostingDto[];
}

export interface IncentivePostResponse {
  postingId: string;
  totalPoints: number;
  totalPaise: number;
  lines: number;
}

export interface SalaryPaymentLineDto {
  id: string;
  kind: 'reporting_incentive' | 'adjustment';
  label: string;
  amountPaise: number;
  createdAt: string;
}
